'use client';
import React, { useState } from 'react';
import { Copy, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

interface DemoCredentialsProps {
  onSelect: (email: string, password: string, role: string) => void;
}

const demoAccounts = [
  { id: 'demo-player', role: 'Player', email: 'player@brainbasket.io', password: 'Player@2026', color: '#10B981' },
  { id: 'demo-creator', role: 'Creator', email: 'creator@brainbasket.io', password: 'Creator@2026', color: '#06B6D4' },
  { id: 'demo-admin', role: 'Admin', email: 'admin@brainbasket.io', password: 'Admin@2026', color: '#F59E0B' },
];

export default function DemoCredentials({ onSelect }: DemoCredentialsProps) {
  const [expanded, setExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (value: string, fieldId: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldId);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="mt-6 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(124, 58, 237, 0.25)', background: 'rgba(124, 58, 237, 0.05)' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-primary/5"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#A78BFA' }}>Demo Accounts</span>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(124,58,237,0.2)', color: '#A78BFA' }}>3 roles</span>
        </div>
        <ChevronDown
          size={14}
          style={{ color: '#A78BFA', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
        />
      </button>

      {expanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-3" style={{ borderColor: 'rgba(124,58,237,0.2)' }}>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Click a role to autofill credentials, or copy them manually.
          </p>
          {demoAccounts.map((account) => (
            <div
              key={account.id}
              className="rounded-lg p-3 transition-all cursor-pointer"
              style={{ background: 'var(--muted)', border: `1px solid ${account.color}25` }}
              onClick={() => onSelect(account.email, account.password, account.role)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${account.color}20`, color: account.color }}>
                  {account.role}
                </span>
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Click row to autofill</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono" style={{ color: 'var(--foreground)' }}>{account.email}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleCopy(account.email, `${account.id}-email`); }}
                    className="p-1 rounded transition-colors hover:bg-primary/10"
                    aria-label="Copy email"
                  >
                    {copiedField === `${account.id}-email`
                      ? <Check size={12} style={{ color: '#10B981' }} />
                      : <Copy size={12} style={{ color: 'var(--muted-foreground)' }} />
                    }
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>{account.password}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleCopy(account.password, `${account.id}-pass`); }}
                    className="p-1 rounded transition-colors hover:bg-primary/10"
                    aria-label="Copy password"
                  >
                    {copiedField === `${account.id}-pass`
                      ? <Check size={12} style={{ color: '#10B981' }} />
                      : <Copy size={12} style={{ color: 'var(--muted-foreground)' }} />
                    }
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}